import { Injectable } from '@angular/core';
import { first, Observable } from 'rxjs';
import * as _ from 'lodash';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { ModalController } from '@ionic/angular';
import * as Sentry from '@sentry/angular';
import {
  GetRtsResultDto,
  RtsDay,
  RtsVehicle
} from '../../dto/get-rts-result.dto';
import { Order, OrderRegistry } from '../../dto/order.dto';
import { BaseCacheService } from '../base-cache.service';
import { Status } from '../../dto/status.enum';
import { tableOrdersInEditStatus, OrderInEditStatusInterface } from '../../db-description';
import { BackupService } from '../backup.service';
import { ConfigService } from '../config.service';
import { AuthService } from '../auth-service.service';
import { AppModeStatus, OfflineModeService } from '../offline-mode.service';
import { HttpRetryableService } from '../http-retryable.service';

interface CheckOrderInLocalStoreResult {
  Id: number;
  Order: Order;
  user: string;
}

/**
 * Слияние серверного заказа с локальными несинхронизированными правками при загрузке заявки. Правило:
 * структуру реестра (какие дни, какие машины) задаёт сервер, но локальные данные, которых на сервере
 * ещё нет, терять нельзя — введённые данные погрузки (вес, номера сертификатов/СДИЗ), подпись конца
 * дня, локально добавленные машины, завершённые дни. Всё это юридически значимо и невосстановимо.
 *
 * Ключ слияния — ТРОЙНОЕ состояние дня (см. {@link OrdersMergeService#mergeOneDay}): не начат /
 * начат-но-не-завершён / завершён; от него зависит, что берём с сервера, а что оставляем локальным.
 * Машины (рейсы) сопоставляются по стабильному {@code id}, а не по позиции в списке.
 */
@Injectable({
  providedIn: 'root'
})
export class OrdersMergeService extends BaseCacheService {

  private readonly baseRestURL: string;

  constructor(
    dbService: NgxIndexedDBService,
    offlineService: OfflineModeService,
    authService: AuthService,
    modalCtrl: ModalController,
    private backupService: BackupService,
    private readonly http: HttpRetryableService,
    private readonly config: ConfigService
  ) {
    super(dbService, offlineService, authService, modalCtrl);
    this.baseRestURL = this.config.baseRestURL;
  }

  /**
   * Точка входа слияния для одной заявки. Сначала обогащаем серверный заказ реестром ТС
   * ({@link #enrichServerOrder}): для активных — тянем РТС с forwarding-service, для завершённых/закрытых
   * — берём локальную копию дней. Затем сливаем с локальным состоянием. Если реестра нет ни на сервере,
   * ни локально — возвращаем заказ с пустым реестром (показывать нечего, но сам заказ виден).
   */
  async mergeOrder(curServerOrder: Order): Promise<Order> {
    const enrichedServerOrder = await this.enrichServerOrder(curServerOrder);

    if (enrichedServerOrder) {
      return this.checkOrderInLocalStore(enrichedServerOrder);
    }

    const localSearchResult = await this.getCurrentLocalOrder(
      curServerOrder.id
    ).toPromise();

    if (localSearchResult?.Order) {
      // Свежих сведений с сервера нет: показанное состояние перевозчика взято из локальной копии и
      // могло устареть (перевозчика назначили, пока мы были без связи). Снимаем его, чтобы экран
      // сказал «реестр не загрузился», а не утверждал, что перевозчика нет.
      const localOrder = localSearchResult.Order;
      localOrder.carrierAssigned = undefined;
      localOrder.carrierName = undefined;
      return localOrder;
    }

    const updatedOrder = _.cloneDeep(curServerOrder);
    updatedOrder.registry = {
      completed: false,
      days: []
    };

    return updatedOrder;
  }

  /**
   * Прикрепляет к серверному заказу реестр ТС (дни/машины). Для завершённых и закрытых заявок
   * ({@code COMPLETED}/{@code CLOSED}) РТС с сервера НЕ запрашиваем — он там уже недоступен; берём
   * локальную копию дней из IndexedDB (в ней хранятся введённые данные и подписи). Для активных всегда
   * тянем актуальный РТС с forwarding-service: вместе с днями он сообщает, назначен ли перевозчик, и по
   * этому признаку экран отличает ожидание от сбоя. Прежнее условие «есть {@code transporterOrderId}»
   * снято: в новом потоке колонка не заполняется, и реестр не запрашивался вовсе. При ошибке возвращаем
   * {@code null} — вызывающий обойдётся локальной версией, не потеряв её.
   */
  async enrichServerOrder(order: Order): Promise<Order | null> {
    try {
      switch (order.status) {
        case Status.COMPLETED:
        case Status.CLOSED: {
          // Завершённые/закрытые: РТС с сервера не тянем (недоступен) — используем копию локальных машин
          // из IndexedDB.
          const localDaysCopy = await this.getLocalDaysCopy(order.id);
          if (localDaysCopy && localDaysCopy.length) {
            if (!order.registry) {
              order.registry = { completed: false, days: [] } as OrderRegistry;
            }
            order.registry.days = localDaysCopy;
          }
          break;
        }
        default: {
          if (this.offlineService.appModeStatus$.value === AppModeStatus.OFFLINE) {
            // Связи заведомо нет: запрос ушёл бы в пять попыток с нарастающими паузами (~5 секунд
            // скелетонов), а закончился бы тем же — локальной версией. Отдаём её сразу.
            return null;
          }
          const res = await this.getRtsByRequestId(order.id).toPromise();

          if (res && res.success) {
            order.registry = {
              completed: false,
              days: res.data?.orders?.map(day => ({ ...day })) || []
            };
            // Поле не пришло (старый сервер) — это «неизвестно», а не «перевозчик не назначен»: иначе
            // экран уверенно сказал бы неправду о заявке, которую везут не первый день.
            order.carrierAssigned = res.data?.carrierAssigned;
            order.carrierName = res.data?.carrierName;
            // Подтверждённое сервером состояние — только когда сервер его знает; «неизвестно» (null) не
            // затирает сохранённое: слияние возьмёт его из локальной копии.
            if (res.data?.carrierAssigned != null) {
              order.carrierAssignmentConfirmed = res.data.carrierAssigned;
            }
            // Номер заказа живёт в trucking, а не в строке заявки. Присваиваем всегда, в том числе
            // пустое: иначе однажды пришедший номер остался бы на экране рядом с «перевозчик не назначен».
            order.transporterOrderId = res.data?.carrierOrderNumber ?? order.transporterOrderId;
          }
          break;
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }

    return order;
  }

  getRtsByRequestId(requestId: number): Observable<GetRtsResultDto> {
    const curUrl = `${this.baseRestURL}forwarding/rts/${requestId}`;
    return this.http.get<GetRtsResultDto>(curUrl);
  }

  /**
   * Соединяет обогащённый серверный заказ с локальным. Есть локальная запись того же заказа — сливаем
   * ({@link #mergeWithLocalOrder}). Локальной записи нет, а заказ активен ({@code ACCEPTED}/{@code IN_WORK})
   * — заводим её из серверной версии ({@link #addOrderWithRtsToLocal}), чтобы дальнейшие правки было куда
   * складывать. В любом исходе возвращаем обогащённый серверный заказ (показ не зависит от записи в БД).
   */
  async checkOrderInLocalStore(enrichedServerOrder: Order): Promise<Order> {
    try {
      const localSearchResult = await this.getCurrentLocalOrder(
        enrichedServerOrder.id
      ).toPromise();

      if (localSearchResult?.Order.id === enrichedServerOrder.id) {
        return this.mergeWithLocalOrder(
          localSearchResult.Order,
          enrichedServerOrder
        );
      }
      if (
        !localSearchResult &&
        (enrichedServerOrder.status === Status.ACCEPTED ||
          enrichedServerOrder.status === Status.IN_WORK)
      ) {
        await this.addOrderWithRtsToLocal(enrichedServerOrder);
      }
    } catch (error) {
      Sentry.captureException(error);
      // console.error('Error checking order in local store');
    }

    return enrichedServerOrder;
  }

  getCurrentLocalOrder(
    orderId: number
  ): Observable<CheckOrderInLocalStoreResult | undefined> {
    return this.dbService
      .getByKey('ordersInEditStatus', orderId)
      .pipe(first()) as Observable<CheckOrderInLocalStoreResult | undefined>;
  }

  /**
   * Основное слияние на уровне заказа. За основу берём серверную версию (глубокая копия), но:
   * - журнал ({@code log}) — из локального (серверный его не несёт);
   * - статус: для {@code IN_WORK}/{@code CLOSED} авторитетен серверный (перевёл сам сервер), иначе
   *   оставляем локальный (локальные переходы, ещё не дошедшие до сервера);
   * - дни реестра сливаем по одному ({@link #mergeOneDay}); серверные дни — основа, но локальные дни,
   *   которых в серверном РТС уже нет, добавляем обратно (см. пояснение у соответствующего блока).
   */
  mergeWithLocalOrder(localOrder: Order, serverOrder: Order) {
    const updatedOrder = _.cloneDeep(serverOrder);

    updatedOrder.log = _.cloneDeep(localOrder.log);

    // Назначение перевозчика: свежее от сервера, а если сервер его не сообщил — сохранённое локально (по нему
    // день погрузки начинается и без связи).
    if (updatedOrder.carrierAssignmentConfirmed == null && localOrder.carrierAssignmentConfirmed != null) {
      updatedOrder.carrierAssignmentConfirmed = localOrder.carrierAssignmentConfirmed;
    }

    // Статус: сервер авторитетен только для IN_WORK/CLOSED; прочие переходы могли произойти локально.
    if (
      serverOrder.status === Status.IN_WORK ||
      serverOrder.status === Status.CLOSED
    ) {
      updatedOrder.status = serverOrder.status;
    } else {
      updatedOrder.status = localOrder.status;
    }

    if (serverOrder.registry?.days?.length) {
      updatedOrder.registry.days = [];

      serverOrder.registry.days.forEach(serverDay => {
        const localDay = this.getDayByIdFromOrder(
          localOrder,
          serverDay.orderNumber
        );

        if (localDay) {
          const mergedDay = this.mergeOneDay(localDay, serverDay);
          updatedOrder.registry.days.push(mergedDay);
        } else {
          updatedOrder.registry.days.push(serverDay);
        }
      });

      // Локальные дни, которых НЕТ в серверном РТС, сохраняем: иначе при изменении реестра на сервере
      // (день перестал отдаваться) терялся бы локальный день — в т.ч. завершённый, с подписью конца дня
      // и введёнными данными погрузки (юридически значимые, невосстановимые данные).
      const serverDayNumbers = new Set(
        serverOrder.registry.days.map(day => day.orderNumber)
      );
      (localOrder.registry?.days ?? []).forEach(localDay => {
        if (!serverDayNumbers.has(localDay.orderNumber)) {
          updatedOrder.registry.days.push(_.cloneDeep(localDay));
        }
      });
    } else if (localOrder.registry?.days) {
      updatedOrder.registry.days = [...localOrder.registry.days];
    }
    return updatedOrder;
  }

  getDayByIdFromOrder(localOrder: Order, dayId: string) {
    // Ключ дня — номер строки РТС. У дня-скелета (перевозчик ещё не подал машины) его нет, и
    // сопоставлять такие дни нельзя: поиск по пустому ключу вернул бы ПЕРВЫЙ безномерной день для
    // каждого серверного, размножив один локальный день по всем датам вместе с его весом и подписью.
    if (!dayId) {
      return undefined;
    }
    const day = localOrder?.registry?.days.find(
      (elm: RtsDay) => elm.orderNumber === dayId
    );

    return day || undefined;
  }

  /**
   * Слияние ОДНОГО дня по его состоянию — самое чувствительное место для сохранности локальных данных:
   * - день НАЧАТ, но не завершён ({@code started && !completed}): машины сливаем помашинно
   *   ({@link #updateVehicles}) — введённые данные погрузки сохраняем, признак {@code started} держим;
   * - день НЕ начат ({@code !started && !completed}): данных погрузки ещё нет, поэтому берём серверный
   *   состав машин плюс локально добавленные ({@link #fullVehicleDataUpdate});
   * - день ЗАВЕРШЁН ({@code completed}): берём ЦЕЛИКОМ локальный — он «заморожен», несёт подпись конца
   *   дня и все введённые данные; серверную версию для такого дня игнорируем.
   */
  mergeOneDay(localDay: RtsDay, serverDay: RtsDay) {
    let updatedDay = _.cloneDeep(serverDay);

    if (localDay.started && !localDay.completed) {
      updatedDay.vehicles = this.updateVehicles(localDay, serverDay);
      updatedDay.started = true;
    }

    if (!localDay.started && !localDay.completed) {
      updatedDay = this.fullVehicleDataUpdate(localDay, serverDay);
    }

    if (localDay.completed) {
      updatedDay = _.cloneDeep(localDay);
    }

    return updatedDay;
  }

  /**
   * Помашинное слияние для НАЧАТОГО дня. Сначала переносим локальные машины, которых нет в серверном
   * составе (добавлены офлайн). Затем по каждой серверной машине: есть локальный рейс с тем же {@code id}
   * — берём его через {@link #updateVehicleToLocal} (сохраняя введённые данные погрузки), нет — берём
   * серверную. Сопоставление строго по {@code id} рейса (строковое сравнение).
   */
  updateVehicles(localDay: RtsDay, serverDay: RtsDay) {
    const newVehicles: RtsVehicle[] = [];

    localDay.vehicles.forEach(localCar => {
      if (
        !serverDay.vehicles.find(
          serverCar => serverCar.id?.toString() === localCar.id?.toString()
        )
      ) {
        newVehicles.push(localCar);
      }
    });

    serverDay.vehicles.forEach(serverVehicle => {
      const curLocalTruckRun = this.getLocalDateVehicleRun(
        localDay,
        serverVehicle
      );

      if (curLocalTruckRun) {
        newVehicles.push(
          this.updateVehicleToLocal(curLocalTruckRun, serverVehicle)
        );
      } else {
        const newCar = _.cloneDeep(serverVehicle);
        newVehicles.push(newCar);
      }
      return newVehicles;
    });

    return newVehicles;
  }

  getLocalDateVehicleRun(localDay: RtsDay, serverVehicle: RtsVehicle) {
    return localDay.vehicles.find(
      car => car.id?.toString() === serverVehicle.id?.toString()
    );
  }

  /**
   * Выбор версии одной машины при слиянии начатого дня: оставляем ЛОКАЛЬНУЮ (в ней введённые данные
   * погрузки). Исключение — у локальной ещё нет {@code loadingData}: тогда берём серверную как более
   * свежую (терять нечего). Опирается на инвариант «loadingData появляется только у начатого рейса».
   */
  updateVehicleToLocal(localVehicle: RtsVehicle, serverVehicle: RtsVehicle) {
    let updatedVehicle = _.cloneDeep(localVehicle);

    if (!localVehicle?.loadingData) {
      updatedVehicle = serverVehicle;
    }

    return updatedVehicle;
  }

  /**
   * Состав машин для НЕ начатого дня: серверные машины + локально уникальные (которых нет на сервере по
   * {@code id}). Данных погрузки в не начатом дне ещё нет, поэтому серверный состав берётся как есть.
   * Примечание: метод правит переданный {@code localDay} НА МЕСТЕ (без глубокой копии); вызывающий
   * ({@link #mergeOneDay}) обращается к нему в этой ветке единожды за слияние дня.
   */
  fullVehicleDataUpdate(localDay: RtsDay, serverDay: RtsDay) {
    const localUniqueVehicles: RtsVehicle[] = [];

    localDay.vehicles.forEach(localCar => {
      if (
        !serverDay.vehicles.find(
          serverCar => serverCar.id?.toString() === localCar.id?.toString()
        )
      ) {
        localUniqueVehicles.push(localCar);
      }
    });

    const updatedDay = localDay;
    updatedDay.vehicles = [...serverDay.vehicles, ...localUniqueVehicles];
    return updatedDay;
  }

  /**
   * НЕ ИСПОЛЬЗУЕТСЯ ни одним вызывающим (проверено по кодовой базе). Оставлен как есть — удаление
   * мёртвого кода вне задачи комментирования. Замысел (по коду): отбросить локальные машины БЕЗ данных
   * погрузки, которых нет в серверном составе, сохранив машины с введёнными данными.
   */
  deleteAbsentLocalTrucks(
    localVehicle: RtsVehicle[],
    serverVehicle: RtsVehicle[]
  ) {
    let updatedVehicle = _.cloneDeep(localVehicle);

    return (updatedVehicle = updatedVehicle.filter(car => {
      // добавить кастомные машины проверку
      if (!car?.loadingData) {
        const matchCar = serverVehicle.find(
          serverCar => car.id?.toString() === serverCar.id?.toString()
        );

        return !!matchCar;
      }
      return true;
    }));
  }

  /**
   * Заводит или обновляет локальную запись заказа из серверной версии. Новую запись создаём с
   * {@code orderBackupVersion = 0} и БЕЗ отправки бэкапа (это ещё не правка пользователя, а первичное
   * наполнение). При обновлении существующей записи бэкап шлём только при наличии значимых данных (хотя
   * бы одна машина в дне) И {@code orderBackupVersion > 0} — то есть когда по заказу уже были локальные
   * правки; иначе первичная заливка серверных данных не должна порождать исходящий бэкап.
   */
  addOrderWithRtsToLocal(enrichedServerOrder: Order) {
    this.dbService
      .getByKey<OrderInEditStatusInterface>(tableOrdersInEditStatus, enrichedServerOrder.id)
      .subscribe({
        next: (existingRecord: OrderInEditStatusInterface | undefined) => {
          if (existingRecord) {
            // Если запись существует, обновляем ее данные
            existingRecord.Order = enrichedServerOrder;

            this.dbService
              .update(tableOrdersInEditStatus, existingRecord)
              .subscribe({
                next: () => {
                  const days = (existingRecord.Order?.registry?.days ?? []) as RtsDay[];
                  const hasMeaningful = Array.isArray(days) && days.some(d => Array.isArray(d.vehicles) && d.vehicles.length > 0);
                  if (hasMeaningful && existingRecord.orderBackupVersion > 0) {
                    void this.backupService.saveBackup(existingRecord.Id);
                  }
                },
                error: error => {
                  Sentry.captureException(error);
                }
              });
          } else {
            // Если записи нет, вставляем новую запись
            const newRecord = {
              Id: enrichedServerOrder.id,
              Order: enrichedServerOrder,
              orderBackupVersion: 0
            };

            this.dbService.add(tableOrdersInEditStatus, newRecord).subscribe({
              next: () => {
                // Не отправляем бэкап при первичном добавлении записи
              },
              error: error => {
                Sentry.captureException(error);
              }
            });
          }
        }
      });
  }

  /**
   * Глубокая копия локальных дней заказа — для завершённых/закрытых заявок, где сервер РТС уже не
   * отдаёт (см. {@link #enrichServerOrder}). Нет записи или дней — {@code null}. Копия, а не ссылка,
   * чтобы дальнейшее слияние не правило хранимый объект.
   */
  private async getLocalDaysCopy(orderId: number): Promise<RtsDay[] | null> {
    try {
      const localSearchResult = await this.getCurrentLocalOrder(orderId).toPromise();
      const localDays = localSearchResult?.Order?.registry?.days;
      if (Array.isArray(localDays) && localDays.length) {
        return _.cloneDeep(localDays);
      }
      return null;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  }
}
